import { FunctionComponent, ReactNode } from "react";

import DuetLink from "@ui/components/navigation/DuetLink";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useCoralDrawerContext } from "@voxmedia/duet-context/context/coral-drawer-context";

/**
 * DuetLink wrapper that adds drawer prerendering functionality.
 * Only adds prerendering when Coral drawer functionality is enabled.
 * Falls back to regular DuetLink when drawer functionality is disabled.
 */
interface Props {
  href: string;
  className?: string;
  onClick?: () => void;
  children: ReactNode;
  variant?: string;
  headline?: string;
  authorName?: string;
}

const CommentDrawerOpenerLink: FunctionComponent<Props> = ({
  href,
  className,
  onClick,
  children,
  variant,
  headline,
  authorName,
}) => {
  const analytics = useAnalytics();

  // Drawer functionality is enabled, add prerendering and drawer-specific analytics
  const { prerenderDrawer } = useCoralDrawerContext();

  const handleClick = () => {
    // Drawer-specific analytics
    switch (variant) {
      case "lede":
      case "fullWidthLede":
      case "splitLede":
      case "quickPostPermalink":
        analytics?.emit({
          event: "coral_comment",
          coral_comment: {
            action: "drawer_open_lede",
          },
        });
        break;
      case "button":
        analytics?.emit({
          event: "coral_comment",
          coral_comment: {
            action: "drawer_open_footer",
          },
        });
        break;
      case "stickyNav":
        analytics?.emit({
          event: "coral_comment",
          coral_comment: {
            action: "drawer_open_nav",
          },
        });
        break;
      case "quickPostRiver":
        analytics?.emit({
          event: "quickpost_click",
          quickpost_click: {
            url: href,
            title: headline,
            author: authorName,
            component_name: variant,
            variant: "comment",
          },
        });
        break;
      case "contentCard":
        analytics?.emit({
          event: "article_click",
          article_click: {
            article_uri: href,
            article_title: headline,
            author: authorName,
            page_type: "content_card",
          },
        });
        break;
      default:
        analytics?.emit({
          event: "article_click",
          article_click: {
            article_uri: href,
            article_title: headline,
            author: authorName,
            page_type: "unknown",
          },
        });
    }

    onClick?.();
  };

  return (
    <DuetLink
      href={href}
      className={className}
      onMouseEnter={prerenderDrawer}
      onClick={handleClick}
    >
      {children}
    </DuetLink>
  );
};

export default CommentDrawerOpenerLink;
